import { useEffect, useRef, useCallback, useState } from 'react'
import { NAV_TITLES } from '#/index'
import './index.scss'

interface NavigationProps {
  contentDivRef: React.RefObject<HTMLDivElement | null>
  content: string
  isDone: boolean
}

/** PRD 模块导航组件 — 仅 Agent 2 显示，点击跳转到对应章节锚点，随内容加载逐步出现 */
export default function Navigation({ contentDivRef, content, isDone }: NavigationProps) {
  const navRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [visible, setVisible] = useState(false)
  const prevContentLenRef = useRef(0)

  const handleNavClick = useCallback(
    (index: number) => {
      if (!isDone) return
      setActiveIndex(index)
      const target = contentDivRef.current?.querySelector(`#prd-anchor-${index}`)
      if (target && contentDivRef.current) {
        const targetTop =
          target.getBoundingClientRect().top -
          contentDivRef.current.getBoundingClientRect().top +
          contentDivRef.current.scrollTop
        contentDivRef.current.scrollTo({ top: targetTop, behavior: 'smooth' })
      }
    },
    [contentDivRef, isDone]
  )

  // 内容从空变为非空时高亮第一项
  useEffect(() => {
    if (prevContentLenRef.current === 0 && content.length > 0) {
      setActiveIndex(0)
    }
    prevContentLenRef.current = content.length
  }, [content])

  // 监听内容区滚动状态，控制导航可见性
  useEffect(() => {
    const el = contentDivRef.current
    if (!el) {
      setVisible(false)
      return
    }

    const check = () => {
      // 使用 requestAnimationFrame 确保在浏览器绘制后测量
      requestAnimationFrame(() => {
        if (el.isConnected) {
          setVisible(el.scrollHeight > el.clientHeight)
        }
      })
    }

    // 初始检查
    check()

    // 内容变化时通过 ResizeObserver 检测
    const observer = new ResizeObserver(() => {
      check()
    })
    observer.observe(el)

    // 内容停止变化后的延迟复查（捕获最终渲染状态）
    const deferredTimer = setTimeout(check, 300)

    return () => {
      observer.disconnect()
      clearTimeout(deferredTimer)
    }
    // content 不放在 deps 中：ResizeObserver 已监听尺寸变化，逐帧重建会引发 60fps thrash
  }, [contentDivRef])

  return (
    <div className="dynamic-nav" ref={navRef} style={{ display: visible ? 'flex' : 'none' }}>
      {NAV_TITLES.map((title, index) => (
        <a
          key={index}
          className={`${index === activeIndex ? 'active' : ''} ${!isDone ? 'disabled' : ''}`}
          onClick={() => handleNavClick(index)}
        >
          {title}
        </a>
      ))}
    </div>
  )
}
